'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { useLogging } from 'src/context/logging';
import { useAuth } from 'src/context/auth';
import { Typography } from 'src/components/PulseUI';
import { ActivityIcon } from '../ActivityIcon';
import { daysSince } from 'src/utils/daysSince';
import { format as formatDate } from 'date-fns';

const formatActivityDate = (dateStr: string): string => {
  if (!dateStr) return '';
  const date = new Date(dateStr.split('T')[0] + 'T00:00:00');
  return isNaN(date.getTime()) ? '' : formatDate(date, 'MM/dd/yyyy');
};
import { AddEditActivity } from '../AddEditActivity';
import {
  useGetActivityTypesQuery,
  useGetActivityCategoriesQuery,
  useGetCompanySettingsQuery,
} from 'src/api/redux';
import { COMPANY_SETTINGS_IDS } from 'src/api/redux/settings/constants';
import useAuthorization from 'src/hooks/authorization/useAuthorization';

const ActivityItem = ({ item, onDelete }) => {
  const log = useLogging();
  const { user } = useAuth();
  const { hasPermission } = useAuthorization();
  const [isEditing, setIsEditing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [text, setText] = useState('');

  const { data: activityTypes } = useGetActivityTypesQuery();

  // Category display
  const canReadCategories = hasPermission('read.activity_categories.company');
  const { data: companySettings } = useGetCompanySettingsQuery(undefined, {
    skip: !user?.company?.id,
  });
  const enabledSetting = companySettings?.results?.find(
    (s) => s.code === COMPANY_SETTINGS_IDS.ACTIVITY_CATEGORY_ENABLED
  );
  const isCategoryEnabled = enabledSetting?.value === 'true';
  const showCategory = isCategoryEnabled && canReadCategories;

  const { data: activityCategories } = useGetActivityCategoriesQuery(
    undefined,
    { skip: !showCategory }
  );
  const categoryLabel = useMemo(() => {
    if (!showCategory || !item?.category) return null;
    return activityCategories?.results?.find((c) => c.id === item.category)
      ?.label;
  }, [showCategory, item?.category, activityCategories]);

  useEffect(() => {
    if (!item || !item.details) return;
    if (item.details?.length > 165 && !isExpanded) {
      // take the first 165 characters of the details
      setText(item.details.slice(0, 165) + '...');
    } else {
      setText(item.details);
    }
  }, [item, isExpanded]);

  const handleExpand = () => {
    setIsExpanded(!isExpanded);
    log.event('Expand Activity', {
      location: 'Activity Item',
      data: item,
    });
  };

  const handleEnableEdit = () => {
    setIsEditing(true);
    log.event('Enable Edit Activity', {
      location: 'Activity Item',
      data: item,
    });
  };

  const handleDelete = () => {
    onDelete(item.id);
  };

  const isTruncated = item?.details?.length > 165;
  const activity = useMemo(() => {
    return activityTypes?.results?.find((i) => String(i.id) === String(item?.type));
  }, [activityTypes, item]);

  return (
    <article
      className="flex items-start gap-4 self-stretch"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid={`activity-item-${item?.id}`}
      aria-label={activity?.label ? `${activity.label} activity` : 'Activity'}
    >
      {isEditing ? (
        <AddEditActivity
          item={item}
          onClose={() => setIsEditing(false)}
          onCancel={() => setIsEditing(false)}
        />
      ) : (
        <>
          <div className="w-8 h-full flex flex-col items-start">
            <ActivityIcon item={activity} />
          </div>
          <div className="w-full">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Typography
                  variant="body2"
                  color="primary"
                  bold
                  className="leading-[1.593rem]"
                >
                  {activity?.label}
                </Typography>
                {categoryLabel && (
                  <>
                    <span className="text-neutral-300" aria-hidden="true">
                      &middot;
                    </span>
                    <Typography
                      variant="caption"
                      color="tertiary"
                      className="leading-[1.243rem]"
                      data-testid="activity-item-category"
                    >
                      {categoryLabel}
                    </Typography>
                  </>
                )}
              </div>

              <Typography
                variant="caption"
                color="tertiary"
                align="right"
                className="leading-[1.243rem]"
              >
                {formatActivityDate(item?.activity_date) || daysSince(item?.created_at)}
              </Typography>
            </div>
            <div className="flex py-1 items-center self-stretch">
              <Typography
                variant="caption"
                color="tertiary"
                className="leading-[1.186rem]"
              >
                {`${item?.owner?.first_name} ${item?.owner?.last_name}`}
              </Typography>
            </div>
            <div className="flex flex-col gap-1 w-full">
              <div className="h-auto self-stretch">
                <Typography
                  variant="body2"
                  color="primary"
                  className="leading-[1.453rem]"
                  data-testid="activity-item-details"
                >
                  {text}
                </Typography>
              </div>
              <div className="flex items-center gap-2">
                {isTruncated && (
                  <button
                    onClick={handleExpand}
                    className="bg-transparent border-0 p-0"
                    aria-expanded={isExpanded}
                    aria-label={isExpanded ? 'Show less details' : 'Show more details'}
                    data-testid="activity-item-expand-button"
                  >
                    <Typography
                      variant="caption"
                      bold
                      className="text-primary-800 leading-[1.186rem] cursor-pointer"
                    >
                      {isExpanded ? 'Read less' : 'Read more'}
                    </Typography>
                  </button>
                )}
                <button
                  onClick={handleEnableEdit}
                  className={`bg-transparent border-0 p-0 transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}
                  aria-label="Edit activity"
                  data-testid="activity-item-edit-button"
                >
                  <Typography
                    variant="caption"
                    bold
                    color="tertiary"
                    className="leading-[1.186rem] cursor-pointer"
                  >
                    Edit
                  </Typography>
                </button>
                <button
                  onClick={handleDelete}
                  className={`bg-transparent border-0 p-0 transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}
                  aria-label="Delete activity"
                  data-testid="activity-item-delete-button"
                >
                  <Typography
                    variant="caption"
                    bold
                    className="text-[#FF561D] leading-[1.186rem] cursor-pointer"
                  >
                    Delete
                  </Typography>
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </article>
  );
};

export default ActivityItem;
